import { useCallback, useMemo } from 'react';

import { DirectoryEntry, DispositionTree, RecordingState } from '../../../@types/connect.type';
import { CCP_URL, CONNECT_CONFIG_NAME, CONNECT_INSTANCE_ID } from '../../../configs/connect';
import { postConnectApi } from '../../../utils/connect-api';
import { writeDirectory } from '../../../utils/connect-storage';

// Action handlers for ConnectProvider — extracted to keep the provider file
// itself focused on lifecycle/subscription wiring.

interface ActionDeps {
  agentRef: React.MutableRefObject<connect.Agent | null>;
  contactRef: React.MutableRefObject<connect.Contact | null>;
  userEmail: string;
  directory: DirectoryEntry[];
  setDirectory: React.Dispatch<React.SetStateAction<DirectoryEntry[]>>;
  setRecordingState: React.Dispatch<React.SetStateAction<RecordingState>>;
}

const requireContactIds = (
  contact: connect.Contact | null
): { contactId: string; initialContactId: string } => {
  if (!contact) throw new Error('No active contact');
  return {
    contactId: contact.getContactId(),
    initialContactId: contact.getOriginalContactId() ?? contact.getContactId(),
  };
};

export const useConnectActions = ({
  agentRef,
  contactRef,
  userEmail,
  directory,
  setDirectory,
  setRecordingState,
}: ActionDeps) => {
  const setStatus = useCallback(
    async (statusName: string): Promise<void> => {
      const agent = agentRef.current;
      if (!agent) throw new Error('Agent not initialised');
      const target = agent.getAgentStates().find((s) => s.name === statusName);
      if (!target) throw new Error(`Unknown agent state: ${statusName}`);
      await new Promise<void>((resolve, reject) => {
        agent.setState(target, {
          success: () => resolve(),
          failure: (msg: string) => reject(new Error(msg || 'setState failed')),
        });
      });
    },
    [agentRef]
  );

  const dial = useCallback(
    async (e164: string): Promise<void> => {
      const agent = agentRef.current;
      if (!agent) throw new Error('Agent not initialised');
      const endpoint = connect.Endpoint.byPhoneNumber(e164);
      await new Promise<void>((resolve, reject) => {
        agent.connect(endpoint, {
          success: () => resolve(),
          failure: (msg: string) => reject(new Error(msg || 'dial failed')),
        });
      });
    },
    [agentRef]
  );

  const hangup = useCallback(async (): Promise<void> => {
    const contact = contactRef.current;
    if (!contact) return;
    const conns = contact.getConnections();
    // End the agent's connection — the customer leg drops automatically.
    const agentConn = conns.find((c) => c.getType() === connect.ConnectionType.AGENT);
    agentConn?.destroy({});
  }, [contactRef]);

  // Accept the currently-routed inbound voice contact. The Streams SDK only
  // ever surfaces one routable contact per agent at a time, so picking the
  // first VOICE entry is sufficient — there's nothing to "select" between.
  // No-op when no contact is ringing; failure surfaces as a thrown Error so
  // callers can render a UI message.
  const acceptCall = useCallback(async (): Promise<void> => {
    const agent = agentRef.current;
    if (!agent) throw new Error('Agent not initialised');
    const incoming = agent.getContacts(connect.ContactType.VOICE)[0];
    if (!incoming) return;
    await new Promise<void>((resolve, reject) => {
      incoming.accept({
        success: () => resolve(),
        failure: (msg: string) => reject(new Error(msg || 'Accept failed')),
      });
    });
  }, [agentRef]);

  const popOut = useCallback((): void => {
    // `noopener,noreferrer` severs the popup's `window.opener` link so the
    // CCP page cannot navigate or read state from this tab. `_blank` rather
    // than a named target avoids reusing the same window across clicks.
    window.open(CCP_URL, '_blank', 'width=400,height=600,resizable=yes,noopener,noreferrer');
  }, []);

  const addDirectoryEntry = useCallback(
    (entry: Omit<DirectoryEntry, 'id' | 'createdAt'>): void => {
      const next: DirectoryEntry[] = [
        ...directory,
        {
          ...entry,
          id:
            typeof crypto.randomUUID === 'function'
              ? crypto.randomUUID()
              : `${Date.now()}-${entry.phoneNumber}`,
          createdAt: new Date().toISOString(),
        },
      ];
      setDirectory(next);
      writeDirectory(userEmail, next);
    },
    [directory, setDirectory, userEmail]
  );

  const updateDirectoryEntry = useCallback(
    (id: string, patch: Partial<Omit<DirectoryEntry, 'id'>>): void => {
      const next = directory.map((d) => (d.id === id ? { ...d, ...patch } : d));
      setDirectory(next);
      writeDirectory(userEmail, next);
    },
    [directory, setDirectory, userEmail]
  );

  const removeDirectoryEntry = useCallback(
    (id: string): void => {
      const next = directory.filter((d) => d.id !== id);
      setDirectory(next);
      writeDirectory(userEmail, next);
    },
    [directory, setDirectory, userEmail]
  );

  // Recording controls — wraps the support lambda which calls the AWS SDK
  // Suspend/Resume APIs. Local state is updated optimistically on the
  // assumption that "successful POST" == "recording state changed".
  const pauseRecording = useCallback(async (): Promise<void> => {
    const { contactId, initialContactId } = requireContactIds(contactRef.current);
    await postConnectApi({
      feature: 'pauseresume',
      action: 'pause',
      InstanceId: CONNECT_INSTANCE_ID,
      ContactId: contactId,
      InitialContactId: initialContactId,
    });
    setRecordingState('paused');
  }, [contactRef, setRecordingState]);

  const resumeRecording = useCallback(async (): Promise<void> => {
    const { contactId, initialContactId } = requireContactIds(contactRef.current);
    await postConnectApi({
      feature: 'pauseresume',
      action: 'resume',
      InstanceId: CONNECT_INSTANCE_ID,
      ContactId: contactId,
      InitialContactId: initialContactId,
    });
    setRecordingState('recording');
  }, [contactRef, setRecordingState]);

  // Disposition (after-call wrap-up) — fetch the category tree for the
  // channel ("voice" / "chat"), then submit the agent's selection +
  // optional notes. Tree key matches the legacy bundle:
  // `${configName}.${channel}` (e.g. `amtek.voice`).
  const fetchDispositionTree = useCallback(
    async (channel: string = 'voice'): Promise<DispositionTree> => {
      const result = await postConnectApi<{ items?: { values?: DispositionTree } }>({
        feature: 'disposition',
        action: 'get',
        key: `${CONNECT_CONFIG_NAME}.${channel}`,
      });
      return result?.items?.values ?? {};
    },
    []
  );

  const submitDisposition = useCallback(
    async (
      contactId: string,
      initialContactId: string,
      categoryPath: string,
      notes: string
    ): Promise<void> => {
      await postConnectApi({
        feature: 'disposition',
        action: 'set.ctr',
        contactId,
        initialContactId,
        instanceId: CONNECT_INSTANCE_ID,
        attributes: { 'n-disposition': categoryPath, 'n-note': notes },
      });
    },
    []
  );

  // Memoise the bag so its identity is stable across renders — the inner
  // callbacks are already useCallback'd, but a fresh object literal each
  // render would still invalidate ConnectProvider's `value` memo and cause
  // every consumer to re-render.
  return useMemo(
    () => ({
      setStatus,
      dial,
      hangup,
      acceptCall,
      popOut,
      addDirectoryEntry,
      updateDirectoryEntry,
      removeDirectoryEntry,
      pauseRecording,
      resumeRecording,
      fetchDispositionTree,
      submitDisposition,
    }),
    [
      setStatus,
      dial,
      hangup,
      acceptCall,
      popOut,
      addDirectoryEntry,
      updateDirectoryEntry,
      removeDirectoryEntry,
      pauseRecording,
      resumeRecording,
      fetchDispositionTree,
      submitDisposition,
    ]
  );
};
